import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import jwt_decode from "jwt-decode";
import { useAuth } from "../../../context/AuthContext";
import { fetchExpectedCollectionDue } from "../../../redux/slice/getOpsMISSlice";
import { uploadExpectedCollectionDue } from "../../../redux/slice/uploadOpsMISSlice";
import {BASE_URL} from '../../../redux/apiConfig'
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import * as XLSX from "xlsx";
import moment from "moment";
import {
  Spin,
  Table,
  Card,
  Input,
  Button,
  Select,
  Alert,
  Upload,
  Radio,
  Tooltip,
  message,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  UploadOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { CSVLink } from "react-csv";
import AsyncStorage from "@react-native-async-storage/async-storage";

const { Option } = Select;

const ExpectedCollectionDue = ({ extraProp }) => {
  const dispatch = useDispatch();
  const { authState, onLogout } = useAuth();

  const [loading, setLoading] = useState(true);
  const [searchField, setSearchField] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [currentLoading, setCurrentLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Upload states
  const [fileList, setFileList] = useState([]);
  const [userUploadType, setUserUploadType] = useState(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadFailedError, setUploadFailedError] = useState(false);
  const [invalidFormatError, setInvalidFormatError] = useState(false);
  const [branchFieldError, setBranchFieldError] = useState(false);

  // Download states
  const [downloadLoading, setDownloadLoading] = useState({
    Expected_Collection: false,
    Expected_Collection_Emi: false
  });

  // Memoize JWT parsing to avoid recalculation on every render
  const jwtData = useMemo(() => {
    const jwtBearer = authState?.token;
    if (!jwtBearer) return null;

    try {
      const jwt_payload = jwt_decode(jwtBearer);
      return {
        jwtBearer,
        jwt_payload,
        jwt_payload_expiry: jwt_payload.exp,
        jwt_user_id: jwt_payload.user_id,
        jwt_user_name: extraProp?.username,
        jwt_payload_group: jwt_payload.group
      };
    } catch (error) {
      console.error("JWT decode error:", error);
      return null;
    }
  }, [authState?.token, extraProp?.username]);

  // Memoize Redux state selectors
  const {
    expectedCollectionData = [],
    expectedCollectionLoading,
    expectedCollectionError,
    paginationData = {},
    tokenExpired,
    latestCreatedAtDateTime
  } = useSelector((state) => ({
    expectedCollectionData: state.opsMIS?.expectedCollectionData || [],
    latestCreatedAtDateTime: state.opsMIS?.latestCreatedAtDateTime,
    expectedCollectionLoading: state.opsMIS?.expectedCollectionLoading,
    expectedCollectionError: state.opsMIS?.expectedCollectionError,
    paginationData: state.opsMIS?.paginationDataExpectedCollectionData || {},
    tokenExpired: state.opsMIS?.data?.detail || ""
  }));

  // Upload state from Redux
  const { 
    uploadLoading, 
    uploadError, 
    uploadSuccess: reduxUploadSuccess 
  } = useSelector((state) => ({
    uploadLoading: state.uploadOpsMIS?.loading || false,
    uploadError: state.uploadOpsMIS?.error || null,
    uploadSuccess: state.uploadOpsMIS?.success || false
  }));

  // Handle token expiry
  const handleTokenExpiry = useCallback(async () => {
    const TOKEN_KEY = "my-jwt";
    try {
      await AsyncStorage.removeItem(TOKEN_KEY);
      onLogout();
      location.reload();
    } catch (error) {
      console.error("Error removing token:", error);
    }
  }, [onLogout]);

  // Check token expiry
  useEffect(() => {
    if (jwtData) {
      const currentTime = Math.floor(Date.now() / 1000);
      if (jwtData.jwt_payload_expiry && currentTime > jwtData.jwt_payload_expiry) {
        handleTokenExpiry();
        return;
      }
    }

    if (tokenExpired === "Signature has expired.") {
      handleTokenExpiry();
      return;
    }
  }, [jwtData, tokenExpired, handleTokenExpiry]);

  // Updated search configuration with all the new search fields
  const searchConfig = useMemo(() => ({
    searchFields: [
      { value: "application_id", label: "Application ID", param: "application_id" },
      { value: "customer_name", label: "Customer Name", param: "customer_name" },
      { value: "branch", label: "Branch", param: "branch" },
      { value: "mobile_number", label: "Mobile Number", param: "mobile_number" },
      { value: "loan_account_number", label: "Loan Account No", param: "loan_account_number" },
      { value: "branch_code", label: "Branch Code", param: "branch_code" },
      { value: "zone", label: "Zone", param: "zone" },
      { value: "name_of_sr_rpm", label: "Name of SR RPM", param: "name_of_sr_rpm" },
      { value: "rpm", label: "RPM", param: "rpm" },
      { value: "name_of_am", label: "Name of AM", param: "name_of_am" },
      { value: "business_associate_id", label: "Business Associate ID", param: "business_associate_id" },
      { value: "business_associate", label: "Business Associate", param: "business_associate" },
      { value: "loan_external_id", label: "Loan External ID", param: "loan_external_id" },
      { value: "umrn", label: "UMRN", param: "umrn" },
      { value: "name", label: "Name", param: "name" },
      { value: "customer_address", label: "Customer Address", param: "customer_address" },
      { value: "customer_id", label: "Customer ID", param: "customer_id" },
      { value: "deceased", label: "Deceased", param: "deceased" },
      { value: "product", label: "Product", param: "product" },
      { value: "installment", label: "Installment", param: "installment" },
      { value: "emi_flag", label: "EMI Flag", param: "emi_flag" },
      { value: "current_dpd", label: "Current DPD", param: "current_dpd" },
      { value: "mode", label: "Mode", param: "mode" },
      { value: "partner", label: "Partner", param: "partner" },
    ],
    placeholder: "Search by selected field..."
  }), []);

  // Function to fetch data with pagination and search params
  const fetchDataWithParams = useCallback(async (page = 1, limit = 10, searchParams = {}) => {
    if (!jwtData?.jwtBearer) return;

    try {
      const params = {
        jwtBearer: jwtData.jwtBearer,
        page,
        limit,
        ...searchParams
      };
      
      await dispatch(fetchExpectedCollectionDue(params));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching expected collection data:", error);
      setLoading(false);
    }
  }, [dispatch, jwtData?.jwtBearer]);

  // Initial data fetch
  useEffect(() => {
    if (jwtData?.jwtBearer) {
      fetchDataWithParams(currentPage, pageSize);
    }
  }, [jwtData?.jwtBearer, currentPage, pageSize, fetchDataWithParams]);

  // Handle search submit
  const handleSearchSubmit = useCallback(async () => {
    if (!searchQuery.trim() || !searchField) return;
    
    setCurrentLoading(true);
    
    try {
      const searchParam = searchConfig.searchFields
        .find(field => field.value === searchField)?.param;
      
      if (searchParam) {
        const searchParams = {
          [searchParam]: searchQuery
        };
        
        setCurrentPage(1);
        await fetchDataWithParams(1, pageSize, searchParams);
        trackFeatureAction("MIS", "Expected Collection Due", "search", { field: searchField });
        setIsSearchActive(true);
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setCurrentLoading(false);
    }
  }, [searchQuery, searchField, searchConfig.searchFields, pageSize, fetchDataWithParams]);

  // Reset search filters
  const resetSearchFilters = useCallback(async () => {
    setSearchQuery("");
    setSearchField("");
    setIsSearchActive(false);
    setCurrentPage(1);
    
    await fetchDataWithParams(1, pageSize);
    trackFeatureAction("MIS", "Expected Collection Due", "reset");
  }, [pageSize, fetchDataWithParams]);

  // Handle pagination change
  const handleTableChange = useCallback((pagination) => {
    const { current, pageSize: newPageSize } = pagination;
    
    setCurrentPage(current);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
    
    // Fetch data with current search params if search is active
    const searchParams = {};
    if (isSearchActive && searchField && searchQuery) {
      const searchParam = searchConfig.searchFields
        .find(field => field.value === searchField)?.param;
      if (searchParam) {
        searchParams[searchParam] = searchQuery;
      }
    }
    
    fetchDataWithParams(
      newPageSize !== pageSize ? 1 : current, 
      newPageSize, 
      searchParams
    );
  }, [pageSize, isSearchActive, searchField, searchQuery, searchConfig.searchFields, fetchDataWithParams]);

  // Exact headers the upload validator requires, in order. MUST stay in sync with
  // column_mapping in mis_ops_funs.py (process_all_report_excel_file): that dict
  // rejects the file if any header is missing, so a typo here becomes a 400 for
  // the user, not a silent mismatch.
  const SAMPLE_HEADERS = [
    "Branch Code", "Branch", "Zone",
    "Name of Sr. RPM", "RPM", "Name of AM",
    "Business Associate id", "Business Associate", "Collector Id",
    "Collector Name", "Loan External Id", "UMRN",
    "Name", "Customer Address", "Applicant phone no",
    "Customer id", "Deceased", "Bank Account Holder Name",
    "Disbursal Account", "IFSC Code", "Product",
    "Loan Account Number", "Due Date", "Installment",
    "EMI Flag", "Excess Amount", "Principal Due",
    "Interest Due", "Fees Due", "Penalty Due",
    "Total Due", "Total Overdue", "Current DPD",
    "Disbursal Date", "Loan Amount", "Last EMI paid date",
    "MODE", "Partner", "Last Month Bouncing Reason",
  ];

  // Empty .xlsx with just the header row, generated client-side (no API call).
  const downloadSampleTemplate = () => {
    try {
      const ws = XLSX.utils.aoa_to_sheet([SAMPLE_HEADERS]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Expected_Collection");
      XLSX.writeFile(wb, "Expected_Collection_Due_Sample.xlsx");
      message.success("Sample template downloaded");
    } catch (error) {
      console.error("Error generating sample template:", error);
      message.error("Could not generate sample template");
    }
  };

  // Download report function
  const downloadReport = async (type) => {
    if (!jwtData?.jwtBearer) {
      message.error("Authentication token not found");
      return;
    }

    setDownloadLoading(prev => ({ ...prev, [type]: true }));

    try {
      const response = await fetch(`${BASE_URL}mis/ops/download/v2/?type=${type}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwtData.jwtBearer}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Download failed');
      }

      // Get the blob data
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `${type}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(`${type} report downloaded successfully`);
      trackFeatureAction("MIS", "Expected Collection Due", "download", { report_type: type });
    } catch (error) {
      console.error(`Error downloading ${type} report:`, error);
      message.error(`Failed to download ${type} report: ${error.message}`);
    } finally {
      setDownloadLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // Handle file change for upload
  const handleFileChange = ({ fileList }) => {
    setFileList(fileList);
    // Reset error states when file changes
    setUploadFailedError(false);
    setInvalidFormatError(false);
    setBranchFieldError(false);
    setUploadSuccess(false);
  };

  // Handle upload type change
  const onUploadTypeChange = (e) => {
    setUserUploadType(e.target.value);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (fileList.length === 0 || !userUploadType) return;
    
    const file = fileList[0].originFileObj;
    const fileName = file.name;
    const fileExtension = fileName.split('.').pop().toLowerCase();
    
    // Validate file format
    if (fileExtension !== 'xlsx') {
      setInvalidFormatError(true);
      return;
    }
    
    setButtonLoading(true);
    setUploadFailedError(false);
    setBranchFieldError(false);
    setUploadSuccess(false);
    
    try {
      const result = await dispatch(uploadExpectedCollectionDue({
        jwt_token: jwtData.jwtBearer,
        type: userUploadType, 
        file: file
      }));
      
      if (uploadExpectedCollectionDue.fulfilled.match(result)) {
        setUploadSuccess(true);

        trackFeatureAction("MIS", "Expected Collection Due", "upload", { upload_type: userUploadType });

        setFileList([]);
        setUserUploadType(null);
        
        // Refresh the data after successful upload
        fetchDataWithParams(currentPage, pageSize);
      } else {
        setUploadFailedError(true);
        
        // Check for specific error messages
        if (result.payload?.message?.includes("Branch")) {
          setBranchFieldError(true);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadFailedError(true);
    } finally {
      setButtonLoading(false);
    }
  };

  // Custom request handler for Antd Upload (to prevent automatic upload)
  const customRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  // Generate columns dynamically from data
  const columns = useMemo(() => {
    if (!expectedCollectionData?.length) return [];

    const allKeys = expectedCollectionData.reduce((keys, record) => {
      return keys.concat(Object.keys(record));
    }, []);

    const uniqueKeys = [...new Set(allKeys)];

    return uniqueKeys.map((key) => ({
      title: <strong>{key.toUpperCase().replace(/_/g, ' ')}</strong>,
      dataIndex: key,
      key: key,
      width: "30%",
      align: "left",
    }));
  }, [expectedCollectionData]);

  // Pagination options
  const paginationOptions = useMemo(() => ({
    current: currentPage,
    pageSize: pageSize,
    total: paginationData?.total_count || 0,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "100"],
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    onChange: (page, size) => {
      handleTableChange({ current: page, pageSize: size });
    },
    onShowSizeChange: (current, size) => {
      handleTableChange({ current: 1, pageSize: size });
    }
  }), [currentPage, pageSize, paginationData?.total_count, handleTableChange]);

  // Handle Enter key press for search
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  }, [handleSearchSubmit]);

  if (!jwtData) {
    return (
      <div className="spinner-container">
        <Spin size="large" />
      </div>
    );
  }

  console.log("jwtData - ", jwtData);
  console.log("jwtData.jwt_payload.group - ", jwtData.jwt_payload.group_id);

  return (
    <center>
      {/* Upload Card - Conditionally rendered based on user group_id */}
  {jwtData.jwt_payload.group_id == "0" ||
            jwtData.jwt_payload.group_id == "1" ||
            jwtData.jwt_payload.group_id == "2" ||
            jwtData.jwt_payload.group_id == "3" ||
            jwtData.jwt_payload.group_id == "4" || jwtData.jwt_payload.group_id == "13" || jwtData.jwt_payload.group_id == "19" ? null : (
        <Card
          style={{
            width: "60%",
            marginTop: "20px",
            boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.2)",
            borderRadius: "5px",
          }}
          bordered={true}
        >
          <center>
            <div>
              <p>
                <strong>
                  <FileExcelOutlined /> UPLOAD EXPECTED COLLECTION DUE REPORT
                </strong>
              </p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Upload
                  customRequest={customRequest}
                  name="file"
                  fileList={fileList}
                  listType="picture-card"
                  onChange={handleFileChange}
                  maxCount={1}
                  style={{alignItems: "center"}}
                  beforeUpload={() => false}
                >
                  {fileList.length === 0 && (
                    <div>
                      <UploadOutlined />
                      <div style={{ marginTop: 8 }}>Select File</div>
                    </div>
                  )}
                </Upload>
              </div>
              <div>
                <p>
                  <strong>Select upload type</strong>
                  <Tooltip
                    title={
                      <span>
                        Fresh -
                        <br />
                        Selecting fresh as upload type will delete the
                        previous data and show the new data.
                        <br /> <br />
                        Append -
                        <br />
                        Selecting append as upload type will add the new
                        data along with the old data.
                      </span>
                    }
                  >
                    <InfoCircleOutlined
                      style={{
                        marginLeft: "5px",
                        cursor: "pointer",
                        color: "dodgerblue",
                        fontSize: "16px",
                      }}
                    />
                  </Tooltip>
                </p>
                <Radio.Group
                  onChange={onUploadTypeChange}
                  value={userUploadType}
                >
                  <Radio value={"Fresh"}>Fresh</Radio>
                  <Radio value={"Append"}>Append</Radio>
                </Radio.Group>
              </div>
              <Button
                className="regular-button"
                style={{
                  color: "white",
                  padding: "10px 20px",
                  borderRadius: "5px",
                  display: "inline-flex",
                  alignItems: "center",
                  fontWeight: "bold",
                  marginTop: "20px",
                }}
                onClick={handleUpload}
                loading={buttonLoading}
                disabled={fileList.length === 0 || userUploadType === null}
              >
                Upload
              </Button>
              <Button
                style={{
                  padding: "10px 20px",
                  borderRadius: "5px",
                  display: "inline-flex",
                  alignItems: "center",
                  fontWeight: "bold",
                  marginTop: "20px",
                  marginLeft: "10px",
                }}
                onClick={downloadSampleTemplate}
              >
                Download Sample
              </Button>
              {branchFieldError === false ? null : (
                <Alert
                  style={{
                    marginTop: "20px",
                    textAlign: "justify",
                    width: "50%",
                  }}
                  message="Error"
                  description="Branch field is missing from the excel sheet."
                  type="error"
                  showIcon
                />
              )}
              {uploadFailedError === false ? null : (
                <Alert
                  style={{
                    marginTop: "20px",
                    textAlign: "justify",
                    width: "50%",
                  }}
                  message="Error"
                  description="File Upload Failed."
                  type="error"
                  showIcon
                />
              )}
              {invalidFormatError === false ? null : (
                <Alert
                  style={{
                    marginTop: "20px",
                    textAlign: "justify",
                    width: "50%",
                  }}
                  message="Error"
                  description="Invalid file format. Allowed formats: xlsx"
                  type="error"
                  showIcon
                />
              )}
              {uploadSuccess === false ? null : (
                <Alert
                  style={{
                    marginTop: "20px",
                    textAlign: "justify",
                    width: "50%",
                  }}
                  message="Success"
                  description="File Uploaded Successfully"
                  type="success"
                  showIcon
                />
              )}
              <div
                style={{
                  textAlign: "justify",
                }}
              >
                <p>
                  <strong>Instructions to be followed -</strong>
                </p>
                <p>1. The extension of the file should be 'xlsx'.</p>
                <p>
                  2. Name of one of the column of the excel file should
                  'Branch' and it should have data in either of the below
                  format - '10001-Jobner' or '10001'.
                </p>
                <p>
                  3. The excel sheet should not be having any formula in any
                  of the cells and cells should only have values in it.
                </p>
              </div>
            </div>
          </center>
        </Card>
      )}

      <Card
        layout="horizontal"
        bordered={true}
        className="criclebox tablespace mb-24"
        style={{ width: "95%", marginTop: "20px" }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            {/* Search Container - Left */}
            <div style={{
           
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap"
              }}>
                {/* Search Field Selector */}
                <Select
                  value={searchField}
                  onChange={setSearchField}
                  style={{
                    width: "180px",
                    height: "44px",
                    borderRadius: "8px",
                    border: "1px solid #e0e6ed",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
                    fontWeight: "500",
                    marginRight: "10px"
                  }}
                  placeholder="Select field"
                  dropdownStyle={{
                    borderRadius: "8px",
                    padding: "8px",
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)"
                  }}
                  size="middle"
                >
                  {searchConfig.searchFields.map((field) => (
                    <Option 
                      key={field.value} 
                      value={field.value}
                      style={{
                        padding: "10px 12px",
                        borderRadius: "6px",
                        margin: "4px 0",
                        transition: "all 0.2s ease"
                      }}
                    >
                      {field.label}
                    </Option>
                  ))}
                </Select>

                {/* Search Input */}
                <Input
                  placeholder={searchConfig.placeholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  allowClear
                  style={{
                    width: "300px",
                    height: "44px",
                    borderRadius: "8px",
                    border: "1px solid #e0e6ed",
                    padding: "0 16px",
                    fontSize: "14px",
                    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
                    transition: "all 0.3s ease",
                    marginRight: "10px"
                  }}
                />

                {/* Search Button */}
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearchSubmit}
                  loading={currentLoading}
                  disabled={!searchQuery.trim() || !searchField}
                  style={{
                    height: "44px",
                    padding: "0 20px",
                    borderRadius: "8px",
                    fontWeight: "500",
                    border: "none",
                    boxShadow: "0 2px 8px rgba(58, 123, 213, 0.3)",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    marginRight: "10px"
                  }}
                >
                  Search
                </Button>

                {/* Reset Button */}
                {isSearchActive && (
                  <Button
                    icon={<CloseOutlined />}
                    onClick={resetSearchFilters}
                    style={{
                      height: "44px",
                      padding: "0 16px",
                      borderRadius: "8px",
                      fontWeight: "500",
                      color: "#ff4d4f",
                      borderColor: "rgba(255, 77, 79, 0.3)",
                      background: "rgba(255, 77, 79, 0.08)",
                      transition: "all 0.3s ease"
                    }}
                  >
                    Reset
                  </Button>
                )}
              </div>
            </div>

            {/* Title - Center */}
        <div style={{ 
  flex: "1 1 auto", 
  textAlign: "center", 
  fontSize: "18px", 
  fontWeight: "bold" 
}}>
  EXPECTED COLLECTION DUE TRACKER   <span style={{ marginLeft: "10px", fontSize: "14px" }}>
      <br />
     <strong>DATE</strong> - {moment(latestCreatedAtDateTime).add(5, 'hours').add(30, 'minutes').format('DD-MM-YYYY')} &nbsp;
<strong>TIME</strong> - {moment(latestCreatedAtDateTime).add(5, 'hours').add(30, 'minutes').format('HH:mm')} (24 HRS)
    </span>
</div>


  

            {/* Download Buttons - Right */}
          <div style={{
  display: "flex",
  gap: "12px",
  flex: "0 0 auto"
}}>
  <Button
    type="primary"
    icon={<DownloadOutlined />}
    onClick={() => downloadReport('Expected_Collection')}
    loading={downloadLoading.Expected_Collection}
    style={{
      height: "40px",
      padding: "0 16px",
      borderRadius: "6px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      background: "linear-gradient(45deg, #ff512f, #dd2476)",
      border: "none"
    }}
  >
    Download Report
  </Button>
  
  <Button
    type="primary"
    icon={<DownloadOutlined />}
    onClick={() => downloadReport('Expected_Collection_Emi')}
    loading={downloadLoading.Expected_Collection_Emi}
    style={{
      height: "40px",
      padding: "0 16px",
      borderRadius: "6px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "6px",
      background: "linear-gradient(45deg, #ff512f, #dd2476)",
      border: "none"
    }}
  >
    Download Report (4-12 EMI Flag)
  </Button>
</div>
          </div>
        }
      >
        <div className="table-responsive">
          {expectedCollectionLoading && !currentLoading ? (
            <div className="spinner-container">
              <Spin size="large" />
            </div>
          ) : expectedCollectionError ? (
            <Alert
              message="Error"
              description="Failed to load expected collection data. Please try again."
              type="error"
              showIcon
            />
          ) : (
            <Table
              columns={columns}
              dataSource={expectedCollectionData || []}
              pagination={paginationOptions}
              bordered
              rowClassName={(record, index) =>
                index % 2 === 0 ? "table-row-light" : "table-row-dark"
              }
              loading={expectedCollectionLoading || currentLoading}
              rowKey={(record, index) => record.id || index}
            />
          )}
        </div>
      </Card>
    </center>
  );
};

export default ExpectedCollectionDue;